import { NavLink, generatePath } from 'react-router-dom';
import {
  Checkbox,
  IconSelected,
  IconClock,
  IconAttention,
  IconBlock,
  IconShow,
  IconDownload,
  IconLoader,
  IconChat,
  IconEdit,
} from '@ui/src';
import {
  ApplicationDocumentStatus,
  NewApplicationStatus,
} from '../../../../../../hoc/new-application/types';
import {
  useLazyGetIndividualApplicationDocumentFileQuery,
  useLazyGetCofApplicationDocumentFileQuery,
} from '../../../../../../hoc/new-application/store/services';
import { DocumentRowTypes } from '../../types';

import ROUTES from '../../../../../../routes';

import cn from '../../../styles.module.css';

const DocumentRow = ({
  document: documentItem,
  setSelected,
  applicationId,
  cof,
  search,
  setSelectedDocument,
  setRenamePopupVisible,
  status,
  renameAvailable,
}: DocumentRowTypes): React.JSX.Element => {
  let statusIcon: React.JSX.Element;

  const [getIndividualApplicationDocumentFile, { isLoading, isFetching }] =
    useLazyGetIndividualApplicationDocumentFileQuery();

  const [
    getCofApplicationDocumentFile,
    { isLoading: cofDocIsLoading, isFetching: cofDocIsFetching },
  ] = useLazyGetCofApplicationDocumentFileQuery();

  const getFile = async () => {
    let response;

    if (cof) {
      response = await getCofApplicationDocumentFile({
        id: applicationId as string,
        documentId: documentItem.id,
      });
    } else {
      response = await getIndividualApplicationDocumentFile({
        id: applicationId as string,
        documentId: documentItem.id,
      });
    }

    if (response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = documentItem.name;
      hiddenElement.click();
    }
  };

  if (documentItem.status === ApplicationDocumentStatus.ACCEPTED) {
    statusIcon = (
      <div className={cn.documentRowAccepted}>
        <IconSelected
          className={cn.documentRowStatusIcon}
          height={9}
          width={13}
        />
      </div>
    );
  } else if (documentItem.status === ApplicationDocumentStatus.EXPIRED) {
    statusIcon = (
      <div className={cn.documentRowExpired}>
        <IconAttention />
      </div>
    );
  } else if (documentItem.status === ApplicationDocumentStatus.REJECTED) {
    statusIcon = (
      <div className={cn.documentRowRejected}>
        <IconBlock />
      </div>
    );
  } else {
    statusIcon = <div className={cn.documentRowNew} />;
  }

  let link = ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_DOCUMENT;

  if (cof) {
    link = ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL_DOCUMENT;
  }

  const linkString = generatePath(link, {
    applicationId: applicationId as string,
    documentId: documentItem.id,
  });

  return (
    <div className={cn.documentRow} id={documentItem.id}>
      <div className={cn.documentRowLeft}>
        <Checkbox
          name={`checkbox-${documentItem.id}`}
          title={documentItem.name}
          type="checkbox"
          checked={documentItem.isSelected}
          labelClassName={cn.selectDocumentLabel}
          onChange={(e) => setSelected(e.target.checked)}
        />
      </div>
      <div className={cn.documentRowRight}>
        <div className={cn.documentRowRightContentLeft}>
          <div
            className={`${cn.documentsRowDate} ${
              documentItem.warning ||
              documentItem.status === ApplicationDocumentStatus.EXPIRED
                ? cn.documentDateExpired
                : ''
            }`}
          >
            {new Date(documentItem.createdAt).toLocaleDateString()}
            {(documentItem.warning ||
              documentItem.status === ApplicationDocumentStatus.EXPIRED) && (
              <IconClock className={cn.expiredIcon} />
            )}
          </div>
          <div
            className={`${cn.documentRowStatus} ${
              documentItem.status === ApplicationDocumentStatus.NEW
                ? cn.documentRowStatusNew
                : ''
            }`}
          >
            <span>
              {documentItem.status}
              {statusIcon}
              {documentItem?.countComments &&
                documentItem?.countComments.count > 0 && (
                  <div className={cn.documentRowCommentIndiciator}>
                    <IconChat
                      className={cn.documentsRowCommentIcon}
                      width={14}
                      height={14}
                    />
                    {documentItem?.countComments.count}
                  </div>
                )}
            </span>
          </div>
        </div>
        {status !== NewApplicationStatus.ACCEPTED &&
          status !== NewApplicationStatus.DECLINED &&
          renameAvailable && (
            <button
              type="button"
              className={cn.renameButton}
              onClick={() => {
                setSelectedDocument(documentItem);
                setRenamePopupVisible(true);
              }}
            >
              <div className={cn.renameButtonContent}>
                <IconEdit />
                Rename
              </div>
            </button>
          )}

        <NavLink to={`${linkString}${search || ''}`} className={cn.showLink}>
          <IconShow />
        </NavLink>
        <button
          type="button"
          className={cn.singleDownloadButton}
          aria-label="Download button"
          onClick={getFile}
        >
          {isLoading || isFetching || cofDocIsLoading || cofDocIsFetching ? (
            <IconLoader width={16} height={16} />
          ) : (
            <IconDownload />
          )}
        </button>
      </div>
    </div>
  );
};

export default DocumentRow;
